import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Award, Users, Zap, DollarSign, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

function WhySection() {
  const reasons = [
    {
      id: '01',
      icon: TrendingUp,
      title: 'Accelerate Your Career Growth',
      description: 'Our courses are designed to fast-track your professional development, giving you the skills to stand out in a competitive job market.',
    },
    {
      id: '02',
      icon: DollarSign,
      title: 'Increase Your Earning Potential',
      description: 'Learn high-demand skills that can significantly boost your income. Our students report an average 30% increase in salary after completing our courses.',
    },
    {
      id: '03',
      icon: Award,
      title: 'Industry-Recognized Certifications',
      description: 'Gain credentials that matter. Our certifications are recognized by top companies, opening doors to new opportunities.',
    },
    {
      id: '04',
      icon: Users,
      title: 'Network with Industry Leaders',
      description: 'Connect with successful professionals and peers. Build relationships that can lead to collaborations, job offers, and business opportunities.',
    },
    {
      id: '05',
      icon: Zap,
      title: 'Practical, Hands-On Learning',
      description: 'Apply what you learn immediately. Our courses focus on real-world projects and skills that you can use to start earning right away.',
    },
    {
      id: '06',
      icon: Globe,
      title: 'Access a Global Marketplace',
      description: 'Learn how to leverage online platforms to offer your skills worldwide. Tap into the booming digital economy and work from anywhere.',
    },
  ];

  return (
    <section className="py-20 sm:py-24 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full mix-blend-multiply filter blur-3xl opacity-50 md:opacity-70"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-green-200/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50 md:opacity-70"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
            Why Choose NOOD?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Everything you need to grow your skills and build your future career
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.id}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl border border-gray-100 transition-all duration-300"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="mb-4">
                <div className="bg-primary/10 text-primary rounded-full p-3 w-14 h-14 flex items-center justify-center">
                  <reason.icon size={28} />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{reason.title}</h3>
              <p className="text-gray-600">{reason.description}</p>
            </motion.div>
          ))}
        </div>
        
        <motion.div
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-xl border border-gray-100 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-6 text-gray-900">Ready to transform your career and increase your income?</h3>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/courses"
                className="bg-primary text-white text-lg px-8 py-3 rounded-full hover:bg-primary/90 transition-all duration-300 inline-flex items-center justify-center shadow-md hover:shadow-lg"
              >
                Explore Our Courses
                <TrendingUp className="ml-2" size={20} />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default WhySection;
